import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
} from '@angular/core';
import {
	FormArray,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	applyValidators,
	CurrencySymbolPipe,
	DialogBase,
	DialogOptions,
} from '@marmon/frontend/common';
import {
	AlgoliaAnalysis,
	Analysis,
	AnalysisCostType,
	AnalysisCostTypes,
	TimePeriod,
} from '@marmon/shared/types/analysis';
import {
	centsToDollars,
	coerceCurrency,
	Currencies,
	Currency,
} from '@marmon/shared/types/common';
import { TuiDay } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiError,
	TuiInput,
	TuiLoader,
	TuiNotification,
	TuiNumberFormat,
} from '@taiga-ui/core';
import {
	TuiButtonLoading,
	TuiChevron,
	TuiDataListWrapper,
	TuiFileLike,
	TuiInputDate,
	TuiInputNumber,
	TuiSelect,
} from '@taiga-ui/kit';
import { AnalysisApiService } from '../../services';
import { savePreferredCurrency } from '../../utils';
import { applyCurrencyToTimePeriods } from '../../utils';

type FormTimePeriod = {
	currency: Currency;
	minDate: TuiDay;
	maxDate: TuiDay;
	overheadCosts: number;
};

type AnalysisForm = {
	currency: Currency;
	name: string;
	timePeriods: FormTimePeriod[];
	file?: TuiFileLike;
	costType: AnalysisCostType;
};

@Component({
	selector: 'mar-edit-analysis-dialog',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TuiInput,
		TranslocoPipe,
		TuiError,
		TuiInputDate,
		TuiInputNumber,
		CurrencySymbolPipe,
		TuiButton,
		TuiButtonLoading,
		TuiLoader,
		TuiNotification,
		TuiSelect,
		TuiChevron,
		TuiDataListWrapper,
		TuiNumberFormat,
	],
	templateUrl: 'edit-analysis-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'relative block' },
})
export class EditAnalysisDialogComponent extends applyValidators(
	DialogBase<Partial<Analysis>, { analysis: AlgoliaAnalysis }>,
) {
	static override DialogOptions = {
		size: 'l',
		header: 'analyses.dialog.edit-analysis.header',
		dismissible: false,
	} as DialogOptions;

	private readonly analysisApiService = inject(AnalysisApiService);
	private readonly cdRef = inject(ChangeDetectorRef);

	readonly costTypes = AnalysisCostTypes;
	readonly currencies = Currencies;

	analysis!: Analysis;

	form: FormGroup | null = null;

	busy = false;

	/**
	 * the absolute maximum day that any of the time periods could possibly be
	 */
	maxDate: TuiDay | null = null;

	/**
	 * the absolute minimum day that any of the time periods could possibly be
	 */
	minDate: TuiDay | null = null;

	get timePeriods() {
		if (!this.form) return null;
		return this.form.get('timePeriods') as FormArray;
	}

	/**
	 * True when the selected currency differs from the one stored on the analysis.
	 * The edit dialog uses this to explain that the change is display-only.
	 */
	get currencyChanged() {
		if (!this.form || !this.analysis) return false;
		return (
			coerceCurrency(this.form.get('currency')?.value) !==
			coerceCurrency(this.analysis.currency)
		);
	}

	constructor() {
		super();
		this.setupForm();
	}

	async setupForm() {
		this.busy = true;
		this.analysis = await this.analysisApiService
			.findById(this.context.data.analysis.objectID)
			.catch((error) => {
				this.onDismiss();
				console.error('EditAnalysisDialog.setupForm', error);
				throw Error('There was an error fetching the analysis');
			});

		this.form = new FormGroup({
			name: new FormControl(this.analysis.name, [
				(field) =>
					this.getRequiredValidator(
						field,
						'analyses.dialog.edit-analysis.form.require-name',
					),
			]),
			costType: new FormControl(this.analysis.costType, [
				(field) => this.getRequiredValidator(field),
			]),
			currency: new FormControl(coerceCurrency(this.analysis.currency), [
				(field) => this.getRequiredValidator(field),
			]),
			timePeriods: new FormArray(
				this.analysis.timePeriods.map((period) =>
					this.createTimePeriodGroup(period),
				),
			),
		});

		this.busy = false;
		this.cdRef.detectChanges();
		this.setDisabledFields();
	}

	setDisabledFields() {
		if (!this.form) throw Error('The form was not found');
		this.form.get('timePeriods')?.disable({ emitEvent: false });
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (!this.form || !this.form.valid || this.busy) return;

		this.busy = true;
		this.form.disable({ emitEvent: false });

		const value = this.form.getRawValue() as AnalysisForm;
		const currency = coerceCurrency(value.currency);

		return this.analysisApiService
			.update(
				{
					name: value.name,
					costType: value.costType,
					id: this.analysis.id,
					currency,
					timePeriods: applyCurrencyToTimePeriods(
						this.analysis.timePeriods,
						currency,
					),
				},
				{ type: 'update', addActivityLog: true },
			)
			.then((value: Partial<Analysis>) => {
				savePreferredCurrency(currency);
				this.onDismiss(value);
			})
			.catch((error) => {
				console.error('EditAnalysisDialog.onSubmit', error);
				throw Error('There was an error updating the analysis');
			})
			.finally(() => {
				this.form?.enable({ emitEvent: false });
				this.setDisabledFields();
			});
	}

	readonly createTimePeriodGroup = (period: TimePeriod) =>
		new FormGroup({
			currency: new FormControl(coerceCurrency(period.currency)),
			minDate: new FormControl(
				TuiDay.fromUtcNativeDate(period.minDate.toDate()),
				[(field) => this.getRequiredValidator(field)],
			),
			maxDate: new FormControl(
				TuiDay.fromUtcNativeDate(period.maxDate.toDate()),
				[(field) => this.getRequiredValidator(field)],
			),
			overheadCosts: new FormControl(
				centsToDollars(period.overheadCostInCents),
				[(field) => this.getRequiredValidator(field)],
			),
		});
}
